import AppSync from "@/services/AppSync"
import { ChannelStore } from "@/types/Channel.interface"
import Message from "@/types/Message.interface"
import Person from "@/types/Person.interface"
import helpers from "@/utility/helpers"

interface State {
    messagesCache: ChannelStore[]
}

const defaults = () => {
    return {
        //  A cache of recently viewed messages
        messagesCache: []
    }
}

export default {
    state: defaults(),
    mutations: {
        messagesPropertyPush(state: State, message: Message): void {
            //  Grab our messages cache
            const cache = state.messagesCache

            //  Find our channel store from this
            const i = cache.findIndex(x => x.channelID == message.channel_id)

            //  If it doesn't exist, do nothing
            if (i === -1) return

            //  Else, get the message from the cache
            const existing =  cache[i].messages.find((x: Message) => x.id == message.id)

            //  If the message does NOT exist, do nothing
            if (!existing) return

            //  Else, update the message (this is for reactivity)
            const updated = Object.assign({}, existing, message)

            //  Update the cache
            cache[i].messages = window.removeDuplicatesById(cache[i].messages, [updated])
        },
        messagesSet(state: State, channelStore: ChannelStore): void {
            //  Get the channel id from the channelStore
            const id = channelStore.channelID

            //  Get out messages cache
            const cache = state.messagesCache

            //  Find the index of the existing channel store
            const i = cache.findIndex(x => x.channelID == id)

            //  If the channel store exists, update it
            if (i > -1) cache[i] = channelStore

            //  Else, add the channel store
            else cache.push(channelStore)

            //  Sort the cached messages threads by 'loaded' timestamp
            cache.sort((a: ChannelStore, b: ChannelStore) => (a.loaded - b.loaded))

            //  If cached messages threads are greater than 20, we need to trim them (so the memory footprint doesn't expand forever)
            if (cache.length > 30) state.messagesCache = cache.slice(-30)
        },
        messagesPush(state: State, messages: Message[]): void {
            for (const message of messages) {
                //  Check for a current list
                const channelStore = state.messagesCache.find(x => x.channelID == message.channel_id)

                //  If the list does NOT exist, do nothing
                if (!channelStore) continue

                //  Else, add the message to the list
                helpers.addItemById(message, channelStore.messages, true)
            }
        },
        messagesCacheRemove(state: State, id: number): void {
            //  Grab all of our message caches
            const cache = state.messagesCache

            //  Search for the existing messages in the cache
            const channelStore = cache.findIndex(x => x.channelID == id)

            //  If it wasn't found, do nothing
            if (channelStore === -1) return

            //  Else, delete it
            state.messagesCache.splice(channelStore, 1)
        },
        messagesRemove(state: State, ids: number[]): void {
            //  Iterate through each channel store
            for (const channelStore of state.messagesCache) {
                //  Grab the messages from our channel store
                let msgs = channelStore.messages

                //  Filter our messages belonging to these ids
                let filtered = msgs.filter((x: Message) => !ids.includes(x.id))

                //  Check if the message cache needs to be updated
                const needsUpdate = msgs.length > filtered.length

                //  If the cache needs an update, do it!
                if (needsUpdate) channelStore.messages = filtered
            }
        },
        messagesReset(state: State): void {
            Object.assign(state, defaults())
        }
    },
    actions: {
        //  Return local store is exists, else fetches the record from the API
        getMessageAuthor({ commit, getters }, message: Message): Promise<Person> {
            //  If the message does NOT have an author_id, return null (this occurs when the message was authored by the system, rather than a user)
            if (!message.author_id) return null

            //  Else, search for the author in the local store
            const author = getters.persons.find(x => x.id === message.author_id)

            //  If we found the author in local store, return it
            if (author) return author

            //  Else, we need to retrieve the author from the backend, assemble the api endpoint
            const url = `${getters.api}/persons/${message.author_id}`

            //  Send the request
            return window.axios.get(url).then(response => {

                //  Save the person to the local store
                commit('personsPush', [response.data])

                //  return the message author
                return response.data

            }).catch(() => null)
        },
        messagesDelete({ getters }, msg: Message): Promise<Message> {
            //  Assemble the API endpoint
            const url = `${getters.api}/messages/${msg.id}`

            //  Send the request
            return window.axios.delete(url).then(response => {
                // dispatch('channelsRefresh')
                return response.data
            }).catch(() => {
                return null
            })
        },
        messagesGetPdf({ getters }, params): Promise<Message[]> {
            //  Assemble the API endpoint
            const url = `${getters.api}/messages/pdf?${window.toUrlParams(params)}`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Return the found messages
                return response.data
            }).catch(() => null)
        },
        messagesPost({ dispatch, getters }, msg: Message): Promise<Message> {
            // Don't send null messages
            if (!msg) return

            //  Get the current subscription
            const sub = getters.subscription

            //  Check if the tenant is past due
            const pastDue = sub.status === 'past_due'

            //  Check if this message is a comment
            const comment = msg.type === 'comment'

            //  If the user is past due AND this isn't a comment then don't allow
            if (pastDue && !comment) {
                //  Pop a dialog informing the user
                dispatch('dialogPastDue')
                //  Return null because this failed
                return null
            }

            //  Assemble the API endpoint
            const url = `${getters.api}/messages`

            //  Send the request
            return window.axios.post(url, msg).then(response => {
                //  Post changes to AppSync service
                AppSync.onMessagesPosted(response.data)

                //  Return our created message
                return response.data
            }).catch(() => null)
        },
        messagesPut({ getters }, msg: Message): Promise<Message> {
            //  Assemble the API endpoint
            const url = `${getters.api}/messages`

            //  Send the request
            return window.axios.put(url, msg).then(response => {
                //  Return our updated message
                return response.data
            }).catch(() => null)
        },
        messagesWhereIn({ getters }, ids: number[]): Promise<Message[]> {
            //  Create our payload
            const payload = { message_ids: ids }

            //  Assemble the API endpoint
            const url = `${getters.api}/messages/wherein`

            //  Send the request
            return window.axios.post(url, payload).then(response => {
                //  Return the found messages
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        messagesCache(state: State): object {
            return state.messagesCache
        }
    }
}
